import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import { KeepoLoader } from "../../../../components/KeepoLoader";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import constants from "../../../../constants";
import { getPreviousPath } from "../../../../hooks/PreviousPath";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { StripeIcon } from "../../../../icons/Stripe";
import { Site } from "../../../../model/site";
import { keepoInternalAnalyticsService } from "../../../../services/analytics";
import { api, Coupon as CouponDetails } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { FlowLayout } from "../../components/FlowLayout";
import { formatBRL, toNumber, YEARLY_SAVING } from "../../pricing";
import { useFlowEditingSite } from "../../useFlowEditingSite";
import { waitForPlus } from "../../waitForPlus";
import { TopLinks } from "../Features/styles";
import { savePendingPayment } from "../PaymentPending/pendingPayment";
import { CREATE_NEW_KEEPO } from "../SiteSelection";
import {
  BillingDetails,
  BillingDetailsValue,
  BillingErrors,
  BillingNeeds,
  BrazilAddress,
  BrazilDocument,
  EMPTY_BILLING,
  validateBilling,
  withSavedBilling,
} from "./BillingDetails";
import { LockIcon } from "./icons";
import { PaymentMethod, PaymentMethods, Plan } from "./PaymentMethods";
import { CardErrors, EMPTY_CARD, StripeProvider, validateCard } from "./StripeCard";
import { Container, Coupon, FieldGrid, Header, Layout, Lock, Main, PlanSwitch, Submit, Summary } from "./styles";
import { Wallet, WalletAvailability, WalletButton, WalletProbe } from "./WalletButton";

// What each payment method needs beyond country and name (see BillingDetails)
const needsOf = (method?: PaymentMethod): BillingNeeds =>
  method === "boleto" ? "address" : method === "pix" ? "document" : "none";

type Props = {
  site?: Site;
  setSite: (site: Site) => void;
};

export const CheckoutPage = (props: Props) => (
  <StripeProvider>
    <Checkout {...props} />
  </StripeProvider>
);

const Checkout = ({ site, setSite }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryUrl = useQueryUrl();
  const prefix = "pages.subscription.checkout";

  const [plan, setPlan] = useState<Plan>(queryUrl.get("plan") === "MONTHLY" ? "MONTHLY" : "ANNUALLY");
  // Nothing picked at first: the chosen method opens up (the card form only once the card is picked)
  const [method, setMethod] = useState<PaymentMethod>();
  const [isMethodMissing, setMethodMissing] = useState(false);
  const [card, setCard] = useState(EMPTY_CARD);
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [billing, setBilling] = useState<BillingDetailsValue>(EMPTY_BILLING);
  const [billingErrors, setBillingErrors] = useState<BillingErrors>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [isActivating, setActivating] = useState(false);
  // Paid, but the PLUS didn't come on in time (the payment's confirmation to the API is late): the Keepo that
  // was paid, so the person can check again instead of paying twice
  const [delayedSiteId, setDelayedSiteId] = useState<string>();
  // The payment is under way: the whole screen waits under the loading (with the wallets, once confirmed in
  // their sheet, which is theirs to show until then)
  const [isProcessing, setProcessing] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  // The Keepo already has a PLUS (running, awaiting its boleto, or cancelled but not over): paying again would
  // charge twice, so its subscription page is offered instead
  const [isAlreadyPlus, setAlreadyPlus] = useState(false);
  // Let a boleto expire unpaid: no boleto until one of theirs clears
  const [isBoletoBlocked, setBoletoBlocked] = useState(false);
  const [isSummaryOpen, setSummaryOpen] = useState(false);
  const [siteTitle, setSiteTitle] = useState<string>();

  const [isCouponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  // A promotion code checked on Stripe for the chosen plan (it goes with the payment, which applies it)
  const [coupon, setCoupon] = useState<CouponDetails>();
  // The code isn't valid, or it couldn't be checked now (so it may still be fine)
  const [couponError, setCouponError] = useState<"invalid" | "unavailable">();
  const [isCheckingCoupon, setCheckingCoupon] = useState(false);

  const isBrazil = billing.country === "BR";
  const isAnnual = plan === "ANNUALLY";
  const siteId = queryUrl.get("site");
  const editorFlow = queryUrl.get("flow");
  const isEditorFlow = editorFlow === "create" || editorFlow === "edit";
  // The Keepo being created stays in the side preview, even after a reload
  useFlowEditingSite(isEditorFlow, siteId, site, setSite);
  const isNewKeepo = !siteId || siteId === CREATE_NEW_KEEPO;
  // A new Keepo gets its id on the first attempt: the next ones (a declined card, another method) keep it, so
  // an attempt that wasn't paid is replaced instead of piling up on Stripe
  const attemptedSiteId = useRef<string>();
  const siteIdForPayment = isNewKeepo ? attemptedSiteId.current : (siteId as string);

  // Reaching the checkout is the "checkout" event (it used to fire on the way to Stripe's page). The billing
  // details come pre-filled: the last ones used (any Keepo), or else the account's name (complete from
  // Google/Meta; signing up by e-mail may give just the first name)
  useEffect(() => {
    api
      .getUser()
      .then(user => {
        keepoInternalAnalyticsService.notifyCheckoutEvent(user, {
          interval: isAnnual ? "year" : "month",
          value: toNumber(isAnnual ? constants.price.year.value : constants.price.month.value),
        });
        setBilling(current => withSavedBilling(current, user.billing, user.name));
        setBoletoBlocked(!!user.boletoBlocked);
      })
      .catch(() => undefined);
    // Once, on arrival: the event reports the plan the checkout opened with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNewKeepo) {
      return;
    }
    api
      .getSites()
      .then(sites => {
        const found = sites.find(item => item.siteId === siteId);
        setSiteTitle((found?.site || found?.draft)?.title);
      })
      .catch(() => undefined);
  }, [isNewKeepo, siteId]);

  const changePlan = (next: Plan) => {
    setPlan(next);
    // A coupon may give a different discount on (or not apply to) the other plan
    if (coupon && next !== plan) {
      applyCoupon(coupon.code, next);
    }
    // Pix and boleto are yearly only
    if (next === "MONTHLY" && (method === "pix" || method === "boleto")) {
      setMethod(undefined);
    }
  };

  const changeBilling = (next: BillingDetailsValue) => {
    setBilling(next);
    // Pix and boleto are Brazilian only
    if (next.country !== "BR" && (method === "pix" || method === "boleto")) {
      setMethod(undefined);
    }
    // Errors go away as soon as the field is fixed, without waiting for another submit
    if (Object.keys(billingErrors).length) {
      setBillingErrors(validateBilling(next, needsOf(method)));
    }
  };

  const applyCoupon = async (code = couponCode, forPlan = plan) => {
    setCheckingCoupon(true);
    try {
      const checked = await api.checkCoupon(code.trim(), forPlan);
      setCoupon(checked);
      setCouponError(undefined);
      // Nothing left to pay: only a card subscription can start with no charge (Pix and boleto need an amount)
      if (checked.total <= 0 && (method === "pix" || method === "boleto")) {
        setMethod(undefined);
      }
    } catch (error) {
      setCoupon(undefined);
      setCouponError(isApiError(error, "INVALID_COUPON") ? "invalid" : "unavailable");
    }
    setCheckingCoupon(false);
  };

  const subtotal = toNumber(isAnnual ? constants.price.year.value : constants.price.month.value);
  const couponDiscount = coupon ? coupon.discount / 100 : 0;
  // A 100% coupon: Pix and boleto can't charge zero, so they aren't offered
  const isCouponFree = !!coupon && coupon.total <= 0;
  const total = formatBRL(subtotal - couponDiscount);

  const renewsOn = formatLocaleDate(dayjs().add(1, isAnnual ? "year" : "month"), i18n.language);
  // Pix and boleto don't charge again by themselves: a new one is sent before the year ends
  const isManualRenewal = method === "pix" || method === "boleto";

  const startSubscription = async () => {
    setAlreadyPlus(false);
    try {
      const intent = await api.createSubscriptionIntent({
        plan,
        siteId: siteIdForPayment,
        billing: { name: billing.name.trim(), country: billing.country },
        coupon: coupon?.code,
      });
      attemptedSiteId.current = intent.siteId;
      return intent;
    } catch (error) {
      setAlreadyPlus(isApiError(error, "ALREADY_PLUS"));
      throw error;
    }
  };

  // Paid: the Keepo turns PLUS when Stripe tells the API (webhook), then the success screen takes over
  const finishPaid = async (paidSiteId: string) => {
    setActivating(true);
    setDelayedSiteId(undefined);
    if (!(await waitForPlus(paidSiteId))) {
      setActivating(false);
      setSubmitting(false);
      setProcessing(false);
      setDelayedSiteId(paidSiteId);
      return;
    }
    const params = new URLSearchParams({ siteId: paidSiteId });
    if (editorFlow) params.set("flow", editorFlow);
    navigate(`/subscription/success?${params}`);
  };

  const checkActivationAgain = () => {
    if (!delayedSiteId) {
      return;
    }
    setSubmitting(true);
    setProcessing(true);
    finishPaid(delayedSiteId);
  };

  const failPayment = (message?: string) => {
    // Stripe's message, already in the person's language (declined card, wrong CVC…)
    setSubmitError(message || t(`${prefix}.errors.generic`));
    setSubmitting(false);
    setProcessing(false);
  };

  // Back from a bank's 3D Secure page (the wallets redirect there when the bank asks): Stripe adds how it went
  // to the address, and the Keepo that was paying is in `?site=`
  const redirectStatus = queryUrl.get("redirect_status");
  const handledRedirect = useRef(false);
  useEffect(() => {
    if (!redirectStatus || handledRedirect.current) {
      return;
    }
    handledRedirect.current = true;
    if (redirectStatus === "succeeded" && siteId && !isNewKeepo) {
      setSubmitting(true);
      setProcessing(true);
      finishPaid(siteId);
    } else {
      setSubmitError(t(`${prefix}.errors.generic`));
    }
    // Only on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Card: the subscription is created on the API, then Stripe confirms the payment (with the bank's 3D Secure
  // when it asks)
  const payWithCard = async () => {
    const cardElement = elements?.getElement(CardNumberElement);
    if (!stripe || !cardElement) {
      setSubmitError(t(`${prefix}.errors.unavailable`));
      return;
    }
    setSubmitting(true);
    setProcessing(true);
    setSubmitError(undefined);
    try {
      const intent = await startSubscription();
      // Nothing to pay (a 100% coupon): the card is saved for the next charges, then the PLUS comes on
      if (!intent.clientSecret) {
        if (intent.setupClientSecret) {
          const { error: setupError } = await stripe.confirmCardSetup(intent.setupClientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: { name: card.name.trim() || billing.name.trim(), address: { country: billing.country } },
            },
          });
          if (setupError) {
            failPayment(setupError.message);
            return;
          }
        }
        await finishPaid(intent.siteId);
        return;
      }
      const { error } = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: card.name.trim() || billing.name.trim(), address: { country: billing.country } },
        },
      });
      if (error) {
        failPayment(error.message);
        return;
      }
      await finishPaid(intent.siteId);
    } catch {
      failPayment();
    }
  };

  // Apple Pay / Google Pay: each wallet's official button, inside its item (see WalletButton). The wallets
  // charge the subscription's first invoice: the plan's price, minus the coupon
  const [availableWallets, setAvailableWallets] = useState<WalletAvailability>({ applePay: false, googlePay: false });
  const walletAmount = coupon ? coupon.total : Math.round(subtotal * 100);
  // Later charges: the plan's price, unless the coupon lasts forever
  const recurringAmount = coupon?.duration === "forever" ? coupon.total : Math.round(subtotal * 100);
  const isWallet = method === "applePay" || method === "googlePay";

  const validateForWallet = () => {
    const nextBillingErrors = validateBilling(billing, "none");
    setBillingErrors(nextBillingErrors);
    return !Object.keys(nextBillingErrors).length;
  };

  const walletButton = (wallet: Wallet) => (
    <WalletButton
      wallet={wallet}
      amount={walletAmount}
      recurringAmount={recurringAmount}
      label={t("pages.subscription.checkout.wallet.label", {
        plan: t(`pages.subscription.features.plans.${isAnnual ? "annually" : "monthly"}.title`),
      })}
      interval={isAnnual ? "year" : "month"}
      validate={validateForWallet}
      createSubscription={startSubscription}
      onStart={() => {
        setSubmitting(true);
        setSubmitError(undefined);
      }}
      onConfirm={() => setProcessing(true)}
      onCancel={() => setSubmitting(false)}
      onPaid={finishPaid}
      onError={failPayment}
    />
  );

  const submitHandler = () => {
    setMethodMissing(!method);
    const nextBillingErrors = validateBilling(billing, needsOf(method));
    const nextCardErrors = method === "card" ? validateCard(card) : {};
    setBillingErrors(nextBillingErrors);
    setCardErrors(nextCardErrors);
    if (!method || Object.keys(nextBillingErrors).length || Object.keys(nextCardErrors).length) {
      return;
    }

    if (method === "card") {
      payWithCard();
      return;
    }

    // The wallets have their own button (inside their item), not this one
    if (isWallet) {
      return;
    }

    payOnce(method);
  };

  // Pix and boleto: a year of PLUS paid at once. The API creates it on Stripe and answers with what to pay;
  // the next screen shows it (and, for Pix, waits for the payment). Boleto turns the Keepo PLUS right away
  const payOnce = async (oneTimeMethod: "pix" | "boleto") => {
    setSubmitting(true);
    setProcessing(true);
    setSubmitError(undefined);
    setAlreadyPlus(false);
    try {
      const payment = await api.createOneTimePayment({
        plan: "ANNUALLY",
        method: oneTimeMethod,
        coupon: coupon?.code,
        siteId: siteIdForPayment,
        billing: {
          name: billing.name.trim(),
          country: billing.country,
          document: billing.document,
          ...(oneTimeMethod === "boleto"
            ? {
                postalCode: billing.postalCode,
                line1: billing.line1,
                number: billing.number,
                line2: billing.line2,
                district: billing.district,
                city: billing.city,
                state: billing.state,
              }
            : {}),
        },
      });
      attemptedSiteId.current = payment.siteId;
      savePendingPayment(payment);
      const params = new URLSearchParams({ payment: payment.paymentIntentId, plan, site: payment.siteId });
      if (editorFlow) params.set("flow", editorFlow);
      navigate(`/subscription/checkout/pending?${params}`);
    } catch (error) {
      if (isApiError(error, "ALREADY_PLUS")) {
        setAlreadyPlus(true);
        setSubmitting(false);
        setProcessing(false);
        return;
      }
      if (isApiError(error, "INVALID_COUPON")) {
        setCoupon(undefined);
        failPayment(t(`${prefix}.coupon.invalid`));
        return;
      }
      if (isApiError(error, "BOLETO_BLOCKED")) {
        setBoletoBlocked(true);
        setMethod(undefined);
        failPayment(t(`${prefix}.errors.boletoBlocked`));
        return;
      }
      failPayment();
    }
  };

  // While processing, the button shows Keepo's loading; the label stays for screen readers
  const submitLabel = isSubmitting
    ? isActivating
      ? t(`${prefix}.submit.activating`)
      : t(`${prefix}.submit.processing`)
    : t(`${prefix}.submit.${method || "card"}`, { value: total });

  const summary = (
    <Summary $open={isSummaryOpen} aria-label={t(`${prefix}.summary.title`)}>
      {/* A single line with the total, the rest on demand */}
      <button
        type="button"
        className="toggle"
        aria-expanded={isSummaryOpen}
        onClick={() => setSummaryOpen(!isSummaryOpen)}>
        <span className="label">
          {isSummaryOpen ? t(`${prefix}.summary.hide`) : t(`${prefix}.summary.show`)}
          <ChevronRightIcon width={10} height={10} color="currentColor" />
        </span>
        <b>
          <small>{t(`pages.subscription.features.plans.${isAnnual ? "annually" : "monthly"}.title`)} · </small>
          {total}
        </b>
      </button>

      <div className="details">
        <div className="keepo">
          <PlusTag>✦ Keepo Plus</PlusTag>
          <span>{isNewKeepo ? t(`${prefix}.summary.newKeepo`) : siteTitle}</span>
        </div>

        <PlanSwitch role="radiogroup" aria-label={t("pages.subscription.features.page.plans.label")}>
          <button type="button" role="radio" aria-checked={isAnnual} onClick={() => changePlan("ANNUALLY")}>
            {t("pages.subscription.features.plans.annually.title")}
            <small>{constants.price.year.discount}</small>
          </button>
          <button type="button" role="radio" aria-checked={!isAnnual} onClick={() => changePlan("MONTHLY")}>
            {t("pages.subscription.features.plans.monthly.title")}
          </button>
        </PlanSwitch>

        <dl>
          {isAnnual ? (
            <>
              <div>
                <dt>{t(`${prefix}.summary.yearlyPlan`)}</dt>
                <dd>{constants.price.year.oldValue}</dd>
              </div>
              <div className="saving">
                <dt>{t(`${prefix}.summary.yearlyDiscount`)}</dt>
                <dd>− {YEARLY_SAVING}</dd>
              </div>
            </>
          ) : (
            <div>
              <dt>{t(`${prefix}.summary.monthlyPlan`)}</dt>
              <dd>{constants.price.month.value}</dd>
            </div>
          )}
          {coupon && (
            <div className="saving">
              <dt>
                {t(`${prefix}.coupon.applied`, { code: coupon.code })}
                {coupon.duration !== "forever" && (
                  <small>
                    {" "}
                    ·{" "}
                    {coupon.duration === "once"
                      ? t(`${prefix}.coupon.once`)
                      : t(`${prefix}.coupon.repeating`, { count: coupon.durationInMonths || 1 })}
                  </small>
                )}
              </dt>
              <dd>− {formatBRL(couponDiscount)}</dd>
            </div>
          )}
        </dl>

        <Coupon>
          {coupon ? (
            <button
              type="button"
              className="link"
              onClick={() => {
                setCoupon(undefined);
                setCouponCode("");
              }}>
              {t(`${prefix}.coupon.remove`)}
            </button>
          ) : isCouponOpen ? (
            <form
              onSubmit={event => {
                event.preventDefault();
                applyCoupon();
              }}>
              <input
                autoFocus
                disabled={isCheckingCoupon}
                placeholder={t(`${prefix}.coupon.placeholder`)}
                value={couponCode}
                onChange={e => {
                  setCouponCode(e.target.value.toUpperCase());
                  setCouponError(undefined);
                }}
              />
              <button
                type="submit"
                disabled={!couponCode.trim() || isCheckingCoupon}
                aria-busy={isCheckingCoupon}
                aria-label={t(`${prefix}.coupon.apply`)}>
                {isCheckingCoupon ? <KeepoLoader size={13} /> : t(`${prefix}.coupon.apply`)}
              </button>
            </form>
          ) : (
            <button type="button" className="link" onClick={() => setCouponOpen(true)}>
              {t(`${prefix}.coupon.open`)}
            </button>
          )}
          {couponError && (
            <small>
              {couponError === "invalid" ? t(`${prefix}.coupon.invalid`) : t(`${prefix}.errors.unavailable`)}
            </small>
          )}
        </Coupon>

        <p className="total">
          <span>{t(`${prefix}.summary.total`)}</span>
          <b>{total}</b>
        </p>
        {isAnnual && !coupon && (
          <p className="renewal">{t(`${prefix}.summary.perMonth`, { value: constants.price.year.perMonth })}</p>
        )}
        <p className="renewal">
          {isManualRenewal
            ? t(`${prefix}.summary.validUntil`, { date: renewsOn })
            : t(`${prefix}.summary.renewsOn`, { date: renewsOn })}
        </p>
      </div>
    </Summary>
  );

  const form = (
    <Main>
      <section>
        <h2>{t(`${prefix}.billing.title`)}</h2>
        <BillingDetails value={billing} onChange={changeBilling} errors={billingErrors} />
      </section>

      <section>
        <h2>{t(`${prefix}.methods.label`)}</h2>
        <PaymentMethods
          availableWallets={availableWallets}
          isBoletoBlocked={isBoletoBlocked}
          walletFields={walletButton}
          plan={plan}
          isBrazil={isBrazil && !isCouponFree}
          method={method}
          onMethodChange={next => {
            setMethod(next);
            setMethodMissing(false);
          }}
          disabled={isSubmitting}
          card={card}
          onCardChange={next => {
            setCard(next);
            if (Object.keys(cardErrors).length) {
              setCardErrors(validateCard(next));
            }
          }}
          cardErrors={cardErrors}
          onSwitchToAnnual={() => changePlan("ANNUALLY")}
          pixFields={
            <FieldGrid>
              <BrazilDocument value={billing} onChange={changeBilling} errors={billingErrors} />
            </FieldGrid>
          }
          boletoFields={
            <FieldGrid>
              <BrazilDocument value={billing} onChange={changeBilling} errors={billingErrors} />
              <BrazilAddress value={billing} onChange={changeBilling} errors={billingErrors} />
            </FieldGrid>
          }
        />
        {isMethodMissing && <p className="method-error">{t(`${prefix}.methods.missing`)}</p>}
      </section>

      <WalletProbe amount={walletAmount} onAvailable={setAvailableWallets} />

      <Submit>
        {/* In the creation flow the button is in the fixed bar, as in the other steps */}
        {!isEditorFlow && !isWallet && !delayedSiteId && (
          <button
            type="button"
            className="cta"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            onClick={submitHandler}>
            {submitLabel}
          </button>
        )}
        {delayedSiteId ? (
          <div className="error" role="alert">
            <b>{t(`${prefix}.errors.activationDelayed.title`)}</b>
            <p>{t(`${prefix}.errors.activationDelayed.text`)}</p>
            <LinkButton onClick={checkActivationAgain}>{t(`${prefix}.errors.activationDelayed.retry`)}</LinkButton>{" "}
            <LinkButton onClick={() => navigate("/sites")}>{t(`${prefix}.errors.activationDelayed.keepos`)}</LinkButton>
          </div>
        ) : isAlreadyPlus ? (
          <p className="error" role="alert">
            {t(`${prefix}.errors.alreadyPlus`)}{" "}
            <LinkButton onClick={() => navigate(`/subscription/detail?site=${encodeURIComponent(siteId as string)}`)}>
              {t(`${prefix}.errors.alreadyPlusAction`)}
            </LinkButton>
          </p>
        ) : (
          submitError && (
            <p className="error" role="alert">
              {submitError}
            </p>
          )
        )}
        <p className="legal">
          {isManualRenewal
            ? t(`${prefix}.legal.manual`, { value: total })
            : t(`${prefix}.legal.${isAnnual ? "yearly" : "monthly"}`, { value: formatBRL(recurringAmount / 100) })}
        </p>
        <p className="secure">
          <LockIcon />
          {t("pages.subscription.paymentInfo")}
          <StripeIcon />
        </p>
      </Submit>
    </Main>
  );

  // A step of creating/editing a Keepo: the flow's look, with the Keepo's preview on the side
  if (isEditorFlow) {
    const featuresParams = new URLSearchParams({ flow: editorFlow as string });
    if (siteId) featuresParams.set("site", siteId);
    return (
      <FlowLayout
        title={t("pages.subscription.flow.checkoutTitle")}
        back={{ label: t("pages.subscription.flow.plusTitle"), to: `/subscription/features?${featuresParams}` }}
        bar={
          <>
            <LinkButton onClick={() => navigate(`/subscription/features?${featuresParams}`)}>
              {t("pages.editor.templates.back")}
            </LinkButton>
            {/* The wallets pay with their own button, inside their item */}
            {!isWallet && !delayedSiteId && (
              <Button type="INFO" size="LG" $loading={isSubmitting} onClick={() => !isSubmitting && submitHandler()}>
                {t(`${prefix}.submit.${method || "card"}`, { value: total })}
              </Button>
            )}
          </>
        }>
        <Layout $flow>
          {/* Nothing changes while paying */}
          <Lock disabled={isSubmitting || !!delayedSiteId}>
            {summary}
            {form}
          </Lock>
        </Layout>
        {isProcessing && <LoadingModal />}
      </FlowLayout>
    );
  }

  return (
    <Container>
      <TopLinks>
        <button type="button" onClick={() => (getPreviousPath() ? navigate(-1) : navigate("/subscription/features"))}>
          <ChevronLeftIcon width={10} height={10} color="currentColor" />
          {t(`${prefix}.back`)}
        </button>
      </TopLinks>

      <Layout>
        <Header>
          <h1>{t(`${prefix}.title`)}</h1>
        </Header>

        <Lock disabled={isSubmitting || !!delayedSiteId}>
          {summary}

          {form}
        </Lock>
      </Layout>
      {isProcessing && <LoadingModal />}
    </Container>
  );
};
